import React, { useEffect, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { DateToLocale, ToFirstCapital } from "../../Utils/helpers";
import { viewSiteVisit } from "../../Redux/Actions/franchiseManagerAction";
import toast from "react-hot-toast";
import MainLayout from "../../Layout/mainLayout";
import HierarchyChain from "./hierarchyChain";
import "../SiteVisit/viewAgentSiteVisit.css";
import "./viewFranchiseManger.css";

const DUMMY = [[37.421998, -122.084], [37.785834, -122.406417]];
const isDummy = (lat, lng) =>
    DUMMY.some(([d0, d1]) => Math.abs(lat - d0) < 0.001 && Math.abs(lng - d1) < 0.001);

/* ── Leaflet map rendered inside a card (loaded via CDN, no npm dep) ── */
const AgentRouteMap = ({ startLocation, agentLocations, dropLocation }) => {
    const mapRef = useRef(null);
    const mapInstanceRef = useRef(null);

    useEffect(() => {
        if (!document.getElementById("leaflet-css")) {
            const link = document.createElement("link");
            link.id = "leaflet-css";
            link.rel = "stylesheet";
            link.href = "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.css";
            document.head.appendChild(link);
        }

        const initMap = () => {
            if (mapInstanceRef.current || !mapRef.current) return;
            const L = window.L;

            const validAgentPoints = (agentLocations || [])
                .filter((p) => !isDummy(p.lat, p.lng))
                .map((p) => [p.lat, p.lng]);

            const startLatLng = startLocation
                ? [startLocation.latitude, startLocation.longitude]
                : validAgentPoints[0] || null;

            // drop_location is GeoJSON format: coordinates = [lng, lat]
            const dropLatLng =
                dropLocation?.coordinates?.length === 2
                    ? [dropLocation.coordinates[1], dropLocation.coordinates[0]]
                    : null;

            if (!startLatLng && validAgentPoints.length === 0 && !dropLatLng) return;

            const allPoints = [
                ...(startLatLng ? [startLatLng] : []),
                ...validAgentPoints,
                ...(dropLatLng ? [dropLatLng] : []),
            ];

            const map = L.map(mapRef.current, { zoomControl: true, scrollWheelZoom: false });
            mapInstanceRef.current = map;

            L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
                attribution: "© OpenStreetMap contributors",
                maxZoom: 19,
            }).addTo(map);

            // ── Start marker (green) ──
            if (startLatLng) {
                const startIcon = L.divIcon({
                    html: `<div class="vsv-map-marker vsv-map-marker--start">
                     <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                       <circle cx="12" cy="12" r="10"/>
                     </svg>
                   </div>`,
                    className: "",
                    iconSize: [32, 32],
                    iconAnchor: [16, 16],
                });
                L.marker(startLatLng, { icon: startIcon })
                    .addTo(map)
                    .bindPopup("<b>Start Location</b>");
            }

            // ── Agent trail polyline ──
            if (validAgentPoints.length > 1) {
                L.polyline(validAgentPoints, {
                    color: "#0D152B",
                    weight: 3,
                    opacity: 0.75,
                    dashArray: "6 4",
                }).addTo(map);
            }

            // ── Agent location dots (numbered) ──
            validAgentPoints.forEach((point, idx) => {
                const isLast = idx === validAgentPoints.length - 1;
                const dotIcon = L.divIcon({
                    html: `<div class="vsv-map-dot-numbered ${isLast ? "vsv-map-dot-numbered--last" : ""}">
                     <span class="vsv-map-dot-number">${idx + 1}</span>
                   </div>`,
                    className: "",
                    iconSize: [22, 22],
                    iconAnchor: [11, 11],
                });
                L.marker(point, { icon: dotIcon })
                    .addTo(map)
                    .bindPopup(isLast ? `<b>Last Known Position</b><br/>Point ${idx + 1}` : `Point ${idx + 1}`);
            });

            // ── Drop location marker (orange pin) ──
            if (dropLatLng) {
                const dropIcon = L.divIcon({
                    html: `<div class="vsv-map-marker vsv-map-marker--drop">
                     <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                       <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z"/>
                       <circle cx="12" cy="10" r="3" fill="white"/>
                     </svg>
                   </div>`,
                    className: "",
                    iconSize: [32, 32],
                    iconAnchor: [16, 16],
                });
                L.marker(dropLatLng, { icon: dropIcon })
                    .addTo(map)
                    .bindPopup(
                        `<b>Drop Location</b><br/><span style="font-size:11px;color:#666">${dropLatLng[0].toFixed(6)}, ${dropLatLng[1].toFixed(6)}</span>`
                    );
            }

            // ── Fit bounds ──
            if (allPoints.length > 1) {
                map.fitBounds(L.latLngBounds(allPoints), { padding: [40, 40] });
            } else {
                map.setView(allPoints[0], 16);
            }
        };

        if (window.L) {
            initMap();
        } else {
            const script = document.createElement("script");
            script.src = "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.js";
            script.onload = initMap;
            document.head.appendChild(script);
        }

        return () => {
            if (mapInstanceRef.current) {
                mapInstanceRef.current.remove();
                mapInstanceRef.current = null;
            }
        };
    }, [startLocation, agentLocations, dropLocation]);

    return <div ref={mapRef} className="vsv-map-container" />;
};

/* ═══════════════════════════════════════════════════════════ */

const ViewFranchiseSiteVisit = () => {
    const { id } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { siteVisitDetail: data, visitDetailLoading } = useSelector(
        (state) => state.franchiseManager || {}
    );

    useEffect(() => {
        dispatch(viewSiteVisit(id)).catch(() => toast.error("Failed to load details"));
    }, [id, dispatch]);

    if (visitDetailLoading && !data) {
        return (
            <MainLayout>
                <div className="vsv-loader-wrap">
                    <div className="vsv-loader" />
                    <p className="vsv-loader-text">Loading site visit data…</p>
                </div>
            </MainLayout>
        );
    }

    if (!data) {
        return (
            <MainLayout>
                <div className="vsv-empty-wrap">
                    <p className="vsv-empty-text">No Site Visit Data Found.</p>
                    <button type="button" className="vsv-back-link" onClick={() => navigate(-1)}>
                        ← Back to Site Visits
                    </button>
                </div>
            </MainLayout>
        );
    }

    const isSelf = data.site_visit_by === "self";
    const h = data.hierarchy || {};
    const validPoints = (data.agent_location || []).filter((p) => !isDummy(p.lat, p.lng));
    const lastPoint = validPoints[validPoints.length - 1];

    const nodes = [
        { label: "Franchise Manager", name: h.franchise?.name },
        { label: "City Head", name: h.city_head?.name },
        { label: "Locality Head", name: h.locality_head?.name },
        { label: "Partner", name: h.agent?.name },
        { label: "Lead", name: h.lead?.name, active: true },
    ];

    const people = [
        ["Franchise Manager", h.franchise],
        ["City Head", h.city_head],
        ["Locality Head", h.locality_head],
        ["Partner", h.agent],
    ];

    const franchiseId = h.franchise?._id;
    const cityHeadId = h.city_head?._id;
    const localityHeadId = h.locality_head?._id;
    const profileLink = {
        "Franchise Manager": franchiseId && `/franchise/view/${franchiseId}`,
        "City Head": cityHeadId && (franchiseId
            ? `/franchise/${franchiseId}/city-heads/${cityHeadId}/view`
            : `/franchise/view/${cityHeadId}`),
        "Locality Head": localityHeadId && (franchiseId && cityHeadId
            ? `/franchise/${franchiseId}/city-heads/${cityHeadId}/locality-heads/${localityHeadId}/view`
            : `/franchise/view/${localityHeadId}`),
        Partner: h.agent?._id && `/franchise/partner/view/${h.agent._id}`,
    };

    return (
        <MainLayout>
            <div className="vsv-wrapper">

                {/* ── Header ── */}
                <div className="vsv-header">
                    <div className="vsv-header-left">
                        <button type="button" className="vsv-back-btn" onClick={() => navigate(-1)}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="15 18 9 12 15 6" />
                            </svg>
                            Back
                        </button>
                        <div className="vsv-header-divider" />
                        <div className="vsv-breadcrumb">Site Visit / List / View</div>
                    </div>
                    <div className={`vsv-status-badge vsv-status-badge--${data.status?.toLowerCase()}`}>
                        {ToFirstCapital(data.status || "")}
                    </div>
                </div>

                {/* ── Hero ── */}
                <div className="vsv-hero-card">
                    <div className="vsv-hero-left">
                        <div>
                            <div className="vsv-hero-label">Project</div>
                            <div className="vsv-hero-value">{data.project?.project_name || "—"}</div>
                        </div>
                        {data.otp && (
                            <div className="vsv-otp-badge">
                                <span className="vsv-otp-label">OTP</span>
                                {data.otp}
                            </div>
                        )}
                    </div>
                    <div className="vsv-hero-meta">
                        <div className="vsv-hero-meta-item">{DateToLocale(data.site_visit_date_time) || "—"}</div>
                        <div className="vsv-hero-meta-item">{data.user?.name || "—"}</div>
                        <div className="vsv-hero-meta-item">{ToFirstCapital(data.site_visit_by) || "—"}</div>
                    </div>
                </div>

                {/* ── Hierarchy ── */}
                <HierarchyChain nodes={nodes} />

                {/* ── Main Grid ── */}
                <div className="vsv-grid-layout">

                    {/* Left Column */}
                    <div className="vsv-col">

                        {data.agent && (
                            <div className="vsv-card">
                                <div className="vsv-card-header">
                                    <span className="vsv-card-badge">01</span>
                                    <h2 className="vsv-card-title">Agent Information</h2>
                                </div>
                                <div className="vsv-info-grid">
                                    {data.agent.profile_image && (
                                        <div className="vsv-info-item" style={{ gridColumn: "1 / -1" }}>
                                            <img
                                                src={data.agent.profile_image}
                                                alt={data.agent.name}
                                                style={{ width: 64, height: 64, borderRadius: "50%", objectFit: "cover", border: "2px solid #e5e7eb" }}
                                            />
                                        </div>
                                    )}
                                    <div className="vsv-info-item">
                                        <span className="vsv-info-label">Full Name</span>
                                        <span className="vsv-info-value">{data.agent.name || "No Data"}</span>
                                    </div>
                                    <div className="vsv-info-item">
                                        <span className="vsv-info-label">Email</span>
                                        <span className="vsv-info-value">{data.agent.email || "No Data"}</span>
                                    </div>
                                    <div className="vsv-info-item">
                                        <span className="vsv-info-label">Contact Number</span>
                                        <span className="vsv-info-value">
                                            {data.agent.country_code} {data.agent.contact_number || "No Data"}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="vsv-card">
                            <div className="vsv-card-header">
                                <span className="vsv-card-badge">02</span>
                                <h2 className="vsv-card-title">Lead Information</h2>
                            </div>
                            <div className="vsv-info-grid">
                                <div className="vsv-info-item">
                                    <span className="vsv-info-label">Full Name</span>
                                    <span className="vsv-info-value">{data.user?.name || "No Data"}</span>
                                </div>
                                <div className="vsv-info-item">
                                    <span className="vsv-info-label">Email</span>
                                    <span className="vsv-info-value">{data.user?.email || "No Data"}</span>
                                </div>
                                <div className="vsv-info-item">
                                    <span className="vsv-info-label">Contact Number</span>
                                    <span className="vsv-info-value">
                                        {data.user?.country_code} {data.user?.contact_number || "No Data"}
                                    </span>
                                </div>
                                <div className="vsv-info-item">
                                    <span className="vsv-info-label">Status</span>
                                    <span className={`vsv-inline-status vsv-inline-status--${data.status?.toLowerCase()}`}>
                                        {ToFirstCapital(data.status)}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="vsv-card">
                            <div className="vsv-card-header">
                                <span className="vsv-card-badge">03</span>
                                <h2 className="vsv-card-title">Project Details</h2>
                            </div>
                            <div className="vsv-info-grid">
                                <div className="vsv-info-item">
                                    <span className="vsv-info-label">Project Name</span>
                                    <span className="vsv-info-value">{data.project?.project_name || "No Data"}</span>
                                </div>
                                <div className="vsv-info-item">
                                    <span className="vsv-info-label">Site Visit By</span>
                                    <span className="vsv-info-value">{ToFirstCapital(data.site_visit_by) || "No Data"}</span>
                                </div>
                                <div className="vsv-info-item">
                                    <span className="vsv-info-label">Visit Date & Time</span>
                                    <span className="vsv-info-value">{DateToLocale(data.site_visit_date_time) || "No Data"}</span>
                                </div>
                                <div className="vsv-info-item">
                                    <span className="vsv-info-label">No. of Guests</span>
                                    <span className="vsv-info-value">{data.no_of_guest ?? 0}</span>
                                </div>
                                {!isSelf && (
                                    <>
                                        <div className="vsv-info-item">
                                            <span className="vsv-info-label">Ride Type</span>
                                            <span className="vsv-info-value">{ToFirstCapital(data.ride_type) || "No Data"}</span>
                                        </div>
                                        <div className="vsv-info-item">
                                            <span className="vsv-info-label">Round Trip</span>
                                            <span className="vsv-info-value">{data.is_round_trip ? "Yes" : "No"}</span>
                                        </div>
                                        <div className="vsv-info-item">
                                            <span className="vsv-info-label">Distance</span>
                                            <span className="vsv-info-value">{data.distance || 0} km</span>
                                        </div>
                                        <div className="vsv-info-item">
                                            <span className="vsv-info-label">Estimated Price</span>
                                            <span className="vsv-info-value">
                                                {data.estimated_price ? `₹${data.estimated_price}` : "No Data"}
                                            </span>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>

                        {data.site_visit_purpose && (
                            <div className="vsv-card">
                                <div className="vsv-card-header">
                                    <span className="vsv-card-badge">04</span>
                                    <h2 className="vsv-card-title">Visit Purpose</h2>
                                </div>
                                <p className="vsv-purpose-text">{data.site_visit_purpose}</p>
                            </div>
                        )}
                    </div>

                    {/* Right Column */}
                    <div className="vsv-col">

                        <div className="vsv-card">
                            <div className="vsv-card-header">
                                <span className="vsv-card-badge">05</span>
                                <h2 className="vsv-card-title">Location Details</h2>
                            </div>
                            <div className="vsv-address-row">
                                {!isSelf && data.pickup_address && (
                                    <div className="vsv-address-item">
                                        <div>
                                            <div className="vsv-address-label">Pickup Address</div>
                                            <div className="vsv-address-val">{data.pickup_address}</div>
                                        </div>
                                    </div>
                                )}
                                <div className="vsv-address-item">
                                    <div>
                                        <div className="vsv-address-label">
                                            {isSelf ? "Project Address" : "Drop Off Address"}
                                        </div>
                                        <div className="vsv-address-val">
                                            {data.drop_address || data.project?.street_address || "No Data"}
                                        </div>
                                    </div>
                                </div>
                                <div className="vsv-address-item">
                                    <div>
                                        <div className="vsv-address-label">Project Address</div>
                                        <div className="vsv-address-val">{data.project?.street_address || "No Data"}</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {!isSelf && (
                            <div className="vsv-card">
                                <div className="vsv-card-header">
                                    <span className="vsv-card-badge">06</span>
                                    <h2 className="vsv-card-title">Service Provider</h2>
                                </div>
                                {data.service_provider ? (
                                    <div className="vsv-info-grid">
                                        <div className="vsv-info-item">
                                            <span className="vsv-info-label">Company Name</span>
                                            <span className="vsv-info-value">{data.service_provider.company_name || "No Data"}</span>
                                        </div>
                                        <div className="vsv-info-item">
                                            <span className="vsv-info-label">Contact</span>
                                            <span className="vsv-info-value">
                                                {data.service_provider.country_code} {data.service_provider.contact_number || "No Data"}
                                            </span>
                                        </div>
                                        <div className="vsv-info-item">
                                            <span className="vsv-info-label">Email</span>
                                            <span className="vsv-info-value">{data.service_provider.email || "No Data"}</span>
                                        </div>
                                        <div className="vsv-info-item">
                                            <span className="vsv-info-label">Address</span>
                                            <span className="vsv-info-value">{data.service_provider.address || "No Data"}</span>
                                        </div>
                                    </div>
                                ) : (
                                    <p className="vsv-no-data">No Service Provider Assigned</p>
                                )}
                            </div>
                        )}

                        {/* Hierarchy people (replaces Franchise Details) */}
                        <div className="vsv-card">
                            <div className="vsv-card-header">
                                <span className="vsv-card-badge">07</span>
                                <h2 className="vsv-card-title">Franchise Hierarchy</h2>
                            </div>
                            <div className="vsv-people-list" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                                {people.map(([label, p]) => (
                                    <div className="vfm-person-card" key={label}>
                                        <div className="vfm-person-avatar">
                                            {(p?.name || "—").slice(0, 2).toUpperCase()}
                                        </div>
                                        <div className="vfm-person-info">
                                            <span className="vfm-detail-label">{label}</span>
                                            <span className="vfm-detail-value">{p?.name || "—"}</span>
                                            <span className="vfm-person-sub">
                                                {[p?.email, p?.contact_number].filter(Boolean).join(" · ")}
                                            </span>
                                        </div>
                                        {profileLink[label] && (
                                            <Link to={profileLink[label]} className="vfm-history-btn vfm-person-btn">
                                                View
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <line x1="5" y1="12" x2="19" y2="12" />
                                                    <polyline points="12 5 19 12 12 19" />
                                                </svg>
                                            </Link>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── Agent Route Map ── */}
                {(data.agent_location?.length > 0 || data.start_location) && (
                    <div className="vsv-card vsv-card--fullwidth" style={{ marginTop: "16px" }}>
                        <div className="vsv-card-header">
                            <span className="vsv-card-badge">08</span>
                            <h2 className="vsv-card-title">Agent Route Map</h2>
                        </div>

                        <div className="vsv-map-legend">
                            <div className="vsv-map-legend-item">
                                <span className="vsv-map-legend-dot vsv-map-legend-dot--start" />
                                Start Location
                            </div>
                            <div className="vsv-map-legend-item">
                                <span className="vsv-map-legend-dot vsv-map-legend-dot--trail" />
                                Agent Trail
                            </div>
                            <div className="vsv-map-legend-item">
                                <span className="vsv-map-legend-dot vsv-map-legend-dot--last" />
                                Last Position
                            </div>
                            {data.drop_location?.coordinates?.length === 2 && (
                                <div className="vsv-map-legend-item">
                                    <span className="vsv-map-legend-dot vsv-map-legend-dot--drop" />
                                    Drop Location
                                </div>
                            )}
                        </div>

                        <AgentRouteMap
                            startLocation={data.start_location}
                            agentLocations={data.agent_location}
                            dropLocation={data.drop_location}
                        />

                        <div className="vsv-map-coords">
                            {data.start_location && (
                                <div className="vsv-map-coord-item">
                                    <span className="vsv-map-coord-label">Start</span>
                                    <span className="vsv-map-coord-val">
                                        {data.start_location.latitude.toFixed(6)}, {data.start_location.longitude.toFixed(6)}
                                    </span>
                                </div>
                            )}
                            {lastPoint && (
                                <div className="vsv-map-coord-item">
                                    <span className="vsv-map-coord-label">Last</span>
                                    <span className="vsv-map-coord-val">
                                        {lastPoint.lat.toFixed(6)}, {lastPoint.lng.toFixed(6)}
                                    </span>
                                </div>
                            )}
                            {data.drop_location?.coordinates?.length === 2 && (
                                <div className="vsv-map-coord-item">
                                    <span className="vsv-map-coord-label">Drop</span>
                                    <span className="vsv-map-coord-val">
                                        {data.drop_location.coordinates[1].toFixed(6)}, {data.drop_location.coordinates[0].toFixed(6)}
                                    </span>
                                </div>
                            )}
                            <div className="vsv-map-coord-item">
                                <span className="vsv-map-coord-label">Points</span>
                                <span className="vsv-map-coord-val">{validPoints.length} tracked</span>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </MainLayout>
    );
};

export default ViewFranchiseSiteVisit;